import { Canvas, useFrame, useThree } from "@react-three/fiber"
import {
  OrbitControls,
  Points,
  PointMaterial,
  useGLTF,
  Html,
} from "@react-three/drei"
import {
  useMemo,
  Suspense,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
} from "react"
import * as THREE from "three"
import { PropertyKey, buildColorBuffer } from "./model-properties"
import { useMeshPoints } from "./mesh-points-hook"
import { useTranslation } from "react-i18next"
import { get3DModelModul } from "../../utils/services/3d.service"
import { useSelector } from "react-redux"
import { selectUser } from "../../utils/redux/userSlice"

function FitCamera({
  object,
  margin = 1.1,
}: {
  object: THREE.Object3D | null
  margin?: number
}) {
  const { camera, controls } = useThree() as any
  useLayoutEffect(() => {
    if (!object) return
    const box = new THREE.Box3().setFromObject(object)
    if (!box.isEmpty()) {
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())
      const maxSize = Math.max(size.x, size.y, size.z)
      const fov = THREE.MathUtils.degToRad(camera.fov ?? 45)
      const distance = (maxSize / 2 / Math.tan(fov / 2)) * margin

      const dir = new THREE.Vector3(1, 0.5, 1).normalize()
      camera.position.copy(center.clone().add(dir.multiplyScalar(distance)))
      camera.near = Math.max(distance / 100, 0.01)
      camera.far = distance * 100
      camera.updateProjectionMatrix()

      controls?.target.copy(center)
      controls?.update()
    }
  }, [object, camera, controls, margin])

  return null
}

function CaptureOnce({
  onCapture,
  ready,
  resetKey,
}: {
  onCapture: (url: string) => void
  ready: boolean
  resetKey: any
}) {
  const { gl } = useThree()
  const capturedRef = useRef(false)
  const warmupFramesRef = useRef(0)

  useEffect(() => {
    capturedRef.current = false
    warmupFramesRef.current = 0
  }, [resetKey])

  useFrame(() => {
    if (!ready || capturedRef.current) return
    if (warmupFramesRef.current < 2) {
      warmupFramesRef.current += 1
      return
    }
    const url = gl.domElement.toDataURL("image/png")
    onCapture(url)
    capturedRef.current = true
  })
  return null
}

const PointsLayer = ({
  nodes,
  selectedProperty,
  onContentReady,
}: {
  selectedProperty: PropertyKey
  nodes: any
  onContentReady?: (ready: boolean) => void
}) => {
  if (!nodes?.Main) return null

  const points = useMeshPoints({
    mesh: nodes.Main as THREE.Mesh,
    sampleCount: 10000,
  })

  const positions = useMemo(
    () => new Float32Array(points.flatMap((p: any) => p.position)),
    [points]
  )

  const colors = useMemo(
    () => buildColorBuffer(points, selectedProperty),
    [points, selectedProperty]
  )

  useEffect(() => {
    onContentReady?.(positions.length > 0)
  }, [positions.length, onContentReady])

  const [hovered, setHovered] = useState<number | null>(null)
  const orbitingRef = useRef(false)

  const pickRef = useRef<THREE.InstancedMesh>(null)
  const pickRadius = 0.02

  useLayoutEffect(() => {
    if (!pickRef.current) return
    const m = new THREE.Matrix4()
    const count = positions.length / 3
    for (let i = 0; i < count; i++) {
      const ix = i * 3
      m.setPosition(positions[ix], positions[ix + 1], positions[ix + 2])
      pickRef.current.setMatrixAt(i, m)
    }
    pickRef.current.instanceMatrix.needsUpdate = true
  }, [positions])

  const onMove = useCallback((e: any) => {
    e.stopPropagation()
    if (orbitingRef.current) return // No fem hover mentre orbit
    setHovered(typeof e.instanceId === "number" ? e.instanceId : null)
  }, [])

  const onOut = useCallback(() => setHovered(null), [])

  const hoveredPosition = useMemo(() => {
    if (hovered == null) return null
    const i = hovered * 3
    return [positions[i], positions[i + 1], positions[i + 2]] as [
      number,
      number,
      number
    ]
  }, [hovered, positions])

  const hoveredValue = useMemo(() => {
    if (hovered == null) return null
    const v = (points as any)[hovered]?.[selectedProperty]
    if (v == null) return null
    return typeof v === "number" ? Number(v.toFixed(3)) : String(v)
  }, [hovered, points, selectedProperty])

  const hoveredColor = useMemo(() => {
    if (hovered == null) return null
    const i = hovered * 3
    return new THREE.Color(colors[i], colors[i + 1], colors[i + 2])
  }, [hovered, colors])

  const { t } = useTranslation()

  return (
    <>
      <instancedMesh
        ref={pickRef}
        args={[undefined as any, undefined as any, positions.length / 3]}
        onPointerMove={onMove}
        onPointerOut={onOut}
      >
        <sphereGeometry args={[pickRadius, 8, 8]} />
        <meshBasicMaterial
          transparent
          opacity={0}
          depthWrite={false}
          depthTest={false}
          colorWrite={false}
        />
      </instancedMesh>

      <Points
        positions={positions}
        colors={colors}
        stride={3}
        limit={positions.length / 3}
        frustumCulled={false}
        renderOrder={1}
      >
        <PointMaterial
          size={0.025}
          vertexColors
          sizeAttenuation
          transparent
          depthWrite={false}
          blending={THREE.NormalBlending}
        />
      </Points>

      {hoveredPosition && (
        <>
          <Points
            positions={new Float32Array(hoveredPosition)}
            stride={3}
            limit={1}
            frustumCulled={false}
            renderOrder={2}
          >
            <PointMaterial
              size={0.08}
              sizeAttenuation
              transparent
              depthWrite={false}
              blending={THREE.NormalBlending}
              color={hoveredColor ?? "white"}
            />
          </Points>

          {hoveredValue != null && (
            <Html
              position={hoveredPosition}
              transform={false}
              style={{
                pointerEvents: "none",
                background: "rgba(0, 0, 0, 0.85)",
                color: "white",
                padding: "6px 10px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: "nowrap",
                transform: "translate(12px, -12px)",
                boxShadow: "0 3px 12px rgba(0,0,0,0.4)",
                backdropFilter: "blur(3px)",
              }}
            >
              <div>
                <strong style={{ fontWeight: 700 }}>
                  {t(String(selectedProperty))}
                </strong>
                : {String(hoveredValue)}
              </div>
            </Html>
          )}
        </>
      )}

      <OrbitControls
        makeDefault
        onStart={() => {
          orbitingRef.current = true
          setHovered(null)
        }}
        onEnd={() => {
          orbitingRef.current = false
        }}
      />
    </>
  )
}

/** Carrega i pinta el model NOMÉS quan hi ha URL */
function ModelContent({
  url,
  selectedProperty,
  onContentReady,
}: {
  url: string
  selectedProperty: PropertyKey
  onContentReady: (ready: boolean) => void
}) {
  const { nodes, scene } = useGLTF(url) as any
  return (
    <>
      <FitCamera object={nodes?.Main ?? scene ?? null} margin={1.1} />
      <PointsLayer
        nodes={nodes}
        selectedProperty={selectedProperty}
        onContentReady={onContentReady}
      />
    </>
  )
}

export default function MeshPointsCanvas({
  selectedProperty,
  token,
  setModelImg,
}: {
  selectedProperty: PropertyKey
  token: string
  setModelImg: (url: string) => void
}) {
  const MODEL_KEYS = [
    "column_flower.glb",
    "column.glb",
    "column_double.glb",
    "banc.glb",
    "awayter_key.glb",
  ] as const

  const [modelUrl, setModelUrl] = useState<string | null>(null) // signed URL quan arribi
  const [contentReady, setContentReady] = useState(false)
  const user = useSelector(selectUser)

  useEffect(() => {
    if (!user?.token) return
    const key = MODEL_KEYS[Math.floor(Math.random() * MODEL_KEYS.length)]

    let cancelled = false
    setContentReady(false)

    get3DModelModul(user.token, key)
      .then((res) => {
        if (cancelled) return
        useGLTF.preload(res.url)
        setModelUrl(res.url)
      })
      .catch((err) => {
        console.error("Error fetching 3D model:", err)
        setModelUrl(null)
      })

    return () => {
      cancelled = true
    }
  }, [user.token, token])

  return (
    <Canvas
      camera={{ fov: 40 }}
      frameloop="demand"
      gl={{ antialias: false, preserveDrawingBuffer: true }}
      dpr={[1, 2]}
      style={{ height: "100%" }}
    >
      <Suspense fallback={null}>
        {modelUrl && (
          <>
            <ModelContent
              url={modelUrl}
              selectedProperty={selectedProperty}
              onContentReady={setContentReady}
            />

            <CaptureOnce
              ready={contentReady}
              resetKey={token}
              onCapture={(url) => setModelImg(url)}
            />
          </>
        )}
      </Suspense>
    </Canvas>
  )
}
